import { Component, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, pairwise, startWith } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { FormActionButtonsComponent, FormDropdownComponent, FormInputComponent } from "../../../../shared/components";
import { genericFloatButtons } from "../../../../shared/components/general/float-action";
import { CommonFloatActionButtonType } from "../../../../shared/components/general/float-action/types";
import { Constants } from "../../../../shared/constants/constants";
import { PageMode } from "../../../../shared/constants/enums";
import { GovScoresPillar, ValidatorModelType, ValueText } from "../../../../shared/models";
import { GovScoresScopesApiService, SystemLayoutService, ValidatorApiService } from "../../../../shared/services";

@Component({
  selector: "adaa-entity-pillars-editor",
  imports: [ReactiveFormsModule, TranslatePipe, FormInputComponent, FormDropdownComponent, FormActionButtonsComponent],
  templateUrl: "./entity-pillars-editor.html",
  styleUrl: "./entity-pillars-editor.scss",
})
export class EntityPillarsEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _validatorApiService = inject(ValidatorApiService);
  private _govScoresScopesApiService = inject(GovScoresScopesApiService);
  private _systemLayoutService = inject(SystemLayoutService);

  private readonly _untilDestroy = AdaaHelper.untilDestroyed();
  private readonly _genericFloatButtons = genericFloatButtons();

  id = input<string>();
  pageMode = input.required<string>();

  private _pillarsCopy = signal<GovScoresPillar[]>([]);

  submitted: boolean = false;
  activeTab = signal<number>(0);
  scopeForm: FormGroup;
  PageMode = PageMode;
  formValidation = signal<ValidatorModelType | undefined>(undefined);
  layouts = signal<ValueText[]>([]);
  entities = signal<ValueText[]>([]);
  pillars = signal<ValueText[]>([]);

  public get displayLabel(): string {
    const nameEN = this.scopeForm.get("nameEN")?.value;
    const nameAE = this.scopeForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => {
        this._router.navigateByUrl("/console");
      },
    });

    this._prepareForm();

    this._initPage();
    this._floatActions();
  }

  public goBack(): void {
    this._router.navigateByUrl("/console/government-scores/entity-pillars");
  }

  public submit(): void {
    this.submitted = true;

    if (!this.scopeForm.valid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    this._govScoresScopesApiService.saveScope(this._prepareDataFormSubmit(this.scopeForm.getRawValue())).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/government-scores/entity-pillars");
      },
    });
  }

  /**
   * This method will get
   * - Layout Types
   * - Entities
   * - Pillars
   */
  private _initPage(): void {
    forkJoin({
      layouts: this._govScoresScopesApiService.getAvailableLayouts(),
      entities: this._govScoresScopesApiService.getEntitiesLinkedToGovScoresPillar(),
      pillars: this._govScoresScopesApiService.getPillarsByEntityId(),
    }).subscribe({
      next: (results) => {
        this.layouts.set(
          AdaaHelper.setDropdownArray(results.layouts.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );

        this.entities.set(
          AdaaHelper.setDropdownArray(results.entities.responseData ?? [], "id", AdaaHelper.getFieldLanguage("name"))
        );

        this._pillarsCopy.set(results.pillars.responseData ?? []);
      },
      complete: () => {
        if (this.id()) this._getScope(+this.id()!);
      },
    });
  }

  private _getScope(id: number): void {
    this._govScoresScopesApiService.getScope(id).subscribe({
      next: (response) => {
        if (response.inError) {
          this._toastrService.error(this._translateService.instant("notification.error.unknown_object"));
          this.goBack();
        }

        this.scopeForm.patchValue(response.responseData);
      },
    });
  }

  private _prepareForm(): void {
    const isViewMode = this.pageMode() === PageMode.view;
    this.scopeForm = this._formBuilder.group({
      id: { value: null, disabled: isViewMode },
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      layoutId: { value: null, disabled: isViewMode },
      entityId: { value: null, disabled: isViewMode },
      pillarId: { value: null, disabled: isViewMode },
      displayOrder: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_GOVERNMENT_SCOPES_FORM, this.scopeForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.scopeForm = response.form;
        },
      });

    this.scopeForm
      .get("entityId")
      ?.valueChanges.pipe(
        // Emit the initial value and subsequent value changes as pairs
        startWith(this.scopeForm.get("entityId")?.value),
        pairwise()
      )
      .subscribe(([previousValue, currentValue]) => {
        // If the previous value exists and is different from the current value
        if (previousValue && previousValue !== currentValue) {
          // Reset related form controls when "entityId" changes
          this.scopeForm.get("pillarId")?.setValue(null);
        }

        this._setPillars(currentValue);
      });
  }

  private _setPillars(entityId: number | undefined) {
    if (entityId) {
      //Set pillars ddl
      const pillars = this._pillarsCopy().filter((e) => e.entityId === entityId);
      this.pillars.set(AdaaHelper.setDropdownArray(pillars, "id", AdaaHelper.getFieldLanguage("pillarTypeName")));

      //Set pillars ddl value
      const pillar = this.pillars().find((e) => e.value === this.scopeForm.get("pillarId")?.value);
      if (!pillar) this.scopeForm.get("pillarId")?.setValue(null);
    } else {
      //Set pillars ddl
      this.pillars.set(
        AdaaHelper.setDropdownArray(this._pillarsCopy(), "id", AdaaHelper.getFieldLanguage("pillarTypeName"))
      );

      //Set pillars ddl value
      this.scopeForm.get("pillarId")?.setValue(null);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _prepareDataFormSubmit(data: any): any {
    if (!data.id) delete data.id;
    return data;
  }

  private _floatActions(): void {
    const actions: CommonFloatActionButtonType[] = [
      {
        key: "print",
        data: undefined,
      },
    ];

    if (this.pageMode() !== PageMode.create)
      actions.push({
        key: "systemLog",
        data: {
          itemId: +this.id()!,
          itemTypeId: [Constants.GOV_SCORES_SCOPE],
        },
      });

    if (this.pageMode() === PageMode.view)
      actions.push({
        key: "edit",
        data: {
          url: `/console/government-scores/entity-pillars/edit/${this.id()}`,
        },
      });

    this._genericFloatButtons(actions);
  }
}
